/**
 * Comparador de dos roles.
 *
 * La pregunta real que resuelve no es «en qué se diferencian» sino «cuál de los
 * dos le doy». Por eso lo primero que se ve es la diferencia —lo que uno permite
 * y el otro no— y lo común queda plegado al final: si arranca listando las 40
 * cosas que ambos comparten, la decisión se pierde en el ruido.
 *
 * Las DOS capas se comparan, no una
 * ---------------------------------
 * Hasta acá esta pantalla comparaba sólo los permisos CRUD (capa 1) y callaba
 * las diferencias de «qué registros ve» (capa 2, las reglas de fila). Era
 * exactamente el silencio más caro posible: dos roles pueden conceder las mismas
 * cuatro operaciones sobre Facturas y uno ver las de toda la organización
 * mientras el otro ve sólo las suyas, y el comparador decía «no hay
 * diferencias». Ahora eso tiene su propio bloque, con el mismo criterio que el
 * resto del panel: se muestra lo que el backend informa y nada se deduce. Un
 * modelo sin regla declarada se dice como lo que es —no hay filtro de filas
 * propio— y no como «ve todo», que sería una afirmación que este archivo no
 * puede sostener.
 */

import { useMemo, useState } from 'react';

import { Badge, Button } from '@/components/shell/primitives';
import type { CrudVerb, Role, RoleCapability, RoleScope } from '@/panel/contract';
import { VERBOS, capacidadesDe, concedeAlgo, indicePorModelo, verbosConcedidos } from '@/panel/contract';
import type { UniversoModelos } from '@/panel/FichaRol';
import { Codigo, Etiqueta, Panel } from '@/panel/ui';
import { ETIQUETA_VERBO, TONO_VERBO, etiquetaDeArea, textoDeAlcance } from '@/panel/vocabulario';

interface Diferencia {
  model: string;
  label: string;
  area: string;
  /** Verbos que concede el rol A y no el B. */
  soloA: CrudVerb[];
  /** Verbos que concede el rol B y no el A. */
  soloB: CrudVerb[];
  /** Verbos que conceden los dos. */
  comunes: CrudVerb[];
}

const VACIA: RoleCapability = {
  model: '',
  label: '',
  labelIsTechnical: true,
  create: false,
  read: false,
  write: false,
  delete: false,
};

function comparar(a: Role, b: Role, universo: UniversoModelos): Diferencia[] {
  const indiceA = indicePorModelo(a);
  const indiceB = indicePorModelo(b);
  const modelos = new Set([...indiceA.keys(), ...indiceB.keys()]);

  const etiquetaDe = (modelo: string, capacidad: RoleCapability): string =>
    capacidad.labelIsTechnical ? (universo.modelos.get(modelo)?.label ?? modelo) : capacidad.label;

  const filas: Diferencia[] = [];
  for (const modelo of modelos) {
    const capA = indiceA.get(modelo) ?? VACIA;
    const capB = indiceB.get(modelo) ?? VACIA;
    const fila: Diferencia = {
      model: modelo,
      label: etiquetaDe(modelo, capA.model === '' ? capB : capA),
      area: universo.modelos.get(modelo)?.area ?? '',
      soloA: VERBOS.filter((verbo) => capA[verbo] && !capB[verbo]),
      soloB: VERBOS.filter((verbo) => capB[verbo] && !capA[verbo]),
      comunes: VERBOS.filter((verbo) => capA[verbo] && capB[verbo]),
    };
    filas.push(fila);
  }

  return filas.sort(
    (x, y) =>
      y.soloA.length + y.soloB.length - (x.soloA.length + x.soloB.length) ||
      x.label.localeCompare(y.label, 'es'),
  );
}

// ---------------------------------------------------------------------------
// Capa 2: qué registros ve cada uno
// ---------------------------------------------------------------------------
/** Una regla ya resuelta a texto, con la identidad que sirve para compararla. */
interface ReglaVisible {
  /** Lo que se muestra: el nombre que le puso el `security.py` del módulo. */
  titulo: string;
  /** La traducción del dominio, cuando aporta algo distinto al título. */
  detalle: string | null;
  /** `true` si el detalle es el dominio crudo y va en monoespaciado. */
  crudo: boolean;
  /** Regla global: restricción dura que se cumple siempre. */
  dura: boolean;
  /** Clave de igualdad. Dos reglas son «la misma» si coinciden en esto. */
  clave: string;
}

function reglaVisible(regla: RoleScope): ReglaVisible {
  const { texto, crudo } = textoDeAlcance(regla.description, regla.domain);
  const titulo = regla.rule !== '' && regla.rule !== '(sin nombre)' ? regla.rule : texto;
  return {
    titulo,
    detalle: titulo === texto ? null : texto,
    crudo,
    dura: regla.global,
    // El dominio manda: es lo que el motor evalúa. El nombre entra igual porque
    // dos reglas con el mismo dominio y distinto nombre se leen distinto.
    clave: `${regla.global ? 'dura' : 'rol'}|${regla.rule}|${JSON.stringify(regla.domain)}`,
  };
}

interface DiferenciaDeAlcance {
  model: string;
  label: string;
  /** Reglas que sólo tiene el rol A. */
  soloA: ReglaVisible[];
  /** Reglas que sólo tiene el rol B. */
  soloB: ReglaVisible[];
  /** `true` si el rol concede alguna operación sobre el modelo (capa 1). */
  abreA: boolean;
  abreB: boolean;
}

/**
 * Diferencias de la capa 2, modelo por modelo.
 *
 * Sólo devuelve los modelos donde el conjunto de reglas NO coincide: si los dos
 * roles arrastran la misma regla global de aislamiento por organización —que es
 * casi todas— no hay nada que decidir ahí.
 */
function compararAlcances(a: Role, b: Role, universo: UniversoModelos): DiferenciaDeAlcance[] {
  const porModelo = (rol: Role): Map<string, ReglaVisible[]> => {
    const mapa = new Map<string, ReglaVisible[]>();
    for (const regla of rol.scopes) {
      const lista = mapa.get(regla.model);
      if (lista === undefined) mapa.set(regla.model, [reglaVisible(regla)]);
      else lista.push(reglaVisible(regla));
    }
    return mapa;
  };

  const reglasA = porModelo(a);
  const reglasB = porModelo(b);
  const capacidadesA = indicePorModelo(a);
  const capacidadesB = indicePorModelo(b);

  const etiquetaDe = (modelo: string): string => {
    const deA = a.scopes.find((regla) => regla.model === modelo && regla.label !== modelo);
    const deB = b.scopes.find((regla) => regla.model === modelo && regla.label !== modelo);
    return deA?.label ?? deB?.label ?? universo.modelos.get(modelo)?.label ?? modelo;
  };

  const filas: DiferenciaDeAlcance[] = [];
  for (const modelo of new Set([...reglasA.keys(), ...reglasB.keys()])) {
    const listaA = reglasA.get(modelo) ?? [];
    const listaB = reglasB.get(modelo) ?? [];
    const clavesA = new Set(listaA.map((regla) => regla.clave));
    const clavesB = new Set(listaB.map((regla) => regla.clave));
    const soloA = listaA.filter((regla) => !clavesB.has(regla.clave));
    const soloB = listaB.filter((regla) => !clavesA.has(regla.clave));
    if (soloA.length === 0 && soloB.length === 0) continue;

    const capA = capacidadesA.get(modelo);
    const capB = capacidadesB.get(modelo);
    filas.push({
      model: modelo,
      label: etiquetaDe(modelo),
      soloA,
      soloB,
      abreA: capA !== undefined && concedeAlgo(capA),
      abreB: capB !== undefined && concedeAlgo(capB),
    });
  }

  return filas.sort((x, y) => x.label.localeCompare(y.label, 'es'));
}

/**
 * La celda de un rol para un modelo.
 *
 * Cuando no hay reglas propias NO se dice «ve todo»: se dice que no tiene un
 * filtro de filas propio para ese modelo, que es lo único que el servidor
 * informó. Y si además el rol ni siquiera abre el modelo se dice eso, que es la
 * diferencia que de verdad importa.
 */
function CeldaDeAlcance({ reglas, abre }: { reglas: ReglaVisible[]; abre: boolean }) {
  if (reglas.length === 0) {
    return (
      <span className="text-xs text-fg-muted">
        {abre ? 'Sin regla propia sobre este modelo.' : 'No abre este modelo.'}
      </span>
    );
  }
  return (
    <ul className="space-y-1.5">
      {reglas.map((regla) => (
        <li key={regla.clave}>
          <span className="flex flex-wrap items-baseline gap-1.5">
            <span className="text-sm">{regla.titulo}</span>
            {regla.dura && <Badge tone="warning">Restricción dura</Badge>}
          </span>
          {regla.detalle !== null && (
            <span className="mt-0.5 block text-xs text-fg-muted">
              {regla.crudo ? <Codigo>{regla.detalle}</Codigo> : regla.detalle}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

function Chips({ verbos }: { verbos: CrudVerb[] }) {
  if (verbos.length === 0) return <span className="text-xs text-fg-faint">—</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {verbos.map((verbo) => (
        <Badge key={verbo} tone={TONO_VERBO[verbo]}>
          {ETIQUETA_VERBO[verbo]}
        </Badge>
      ))}
    </span>
  );
}

/** Cuenta cuántas operaciones concede un rol en total: da la escala de un vistazo. */
function totalDeOperaciones(rol: Role): number {
  return capacidadesDe(rol).reduce(
    (total, { capability }) => total + (concedeAlgo(capability) ? verbosConcedidos(capability).length : 0),
    0,
  );
}

export function ComparadorRoles({
  a,
  b,
  universo,
  onCerrar,
}: {
  a: Role;
  b: Role;
  universo: UniversoModelos;
  onCerrar: () => void;
}) {
  const [verComunes, setVerComunes] = useState(false);
  const filas = useMemo(() => comparar(a, b, universo), [a, b, universo]);
  const alcances = useMemo(() => compararAlcances(a, b, universo), [a, b, universo]);

  const conDiferencia = filas.filter((fila) => fila.soloA.length > 0 || fila.soloB.length > 0);
  const iguales = filas.filter(
    (fila) => fila.soloA.length === 0 && fila.soloB.length === 0 && fila.comunes.length > 0,
  );

  return (
    <div className="space-y-4">
      <Panel
        actions={
          <Button variant="secondary" size="sm" onClick={onCerrar}>
            Cerrar comparación
          </Button>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          {[a, b].map((rol, indice) => (
            <div key={rol.code} className="rounded-md border border-line bg-app-subtle px-3 py-2.5">
              <p className="text-2xs font-semibold uppercase tracking-[0.08em] text-fg-faint">
                {indice === 0 ? 'Rol A' : 'Rol B'}
              </p>
              <p className="mt-0.5 text-base font-semibold">{rol.name}</p>
              <p className="mt-1">
                <Codigo>{rol.code}</Codigo>
              </p>
              <p className="mt-2 text-xs text-fg-muted">
                {rol.userCount === null ? 'Personas: sin dato' : `${rol.userCount} persona(s)`} ·{' '}
                {totalDeOperaciones(rol)} operaciones concedidas
              </p>
            </div>
          ))}
        </div>
      </Panel>

      <Panel
        title="Qué puede hacer cada uno"
        subtitle={
          conDiferencia.length === 0
            ? 'No hay diferencias de permisos entre los dos'
            : `${conDiferencia.length} ${conDiferencia.length === 1 ? 'cosa distinta' : 'cosas distintas'}`
        }
        bodyClassName="p-0"
      >
        {conDiferencia.length === 0 ? (
          <p className="p-4 text-sm text-fg-muted">
            Los dos roles conceden exactamente las mismas operaciones.
            {alcances.length > 0
              ? ' Pero no ven las mismas filas: la diferencia está en el bloque de abajo.'
              : ' Tampoco difieren en qué registros ven.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[36rem] text-left">
              <caption className="sr-only">
                Diferencias de permisos entre {a.name} y {b.name}
              </caption>
              <thead>
                <tr className="border-b border-line bg-app-subtle text-2xs uppercase tracking-[0.08em] text-fg-faint">
                  <th scope="col" className="px-4 py-2 font-semibold">
                    Qué
                  </th>
                  <th scope="col" className="px-4 py-2 font-semibold">
                    Sólo {a.name}
                  </th>
                  <th scope="col" className="px-4 py-2 font-semibold">
                    Sólo {b.name}
                  </th>
                </tr>
              </thead>
              <tbody>
                {conDiferencia.map((fila) => (
                  <tr key={fila.model} className="border-b border-line/60 align-top last:border-b-0">
                    <th scope="row" className="px-4 py-2 font-normal">
                      <span className="block text-sm">{fila.label}</span>
                      {fila.area !== '' && (
                        <span className="text-xs text-fg-faint">{etiquetaDeArea(fila.area, universo.areas.get(fila.area))}</span>
                      )}
                    </th>
                    <td className="px-4 py-2">
                      <Chips verbos={fila.soloA} />
                    </td>
                    <td className="px-4 py-2">
                      <Chips verbos={fila.soloB} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* Capa 2. Va DESPUÉS de los permisos y antes de lo común porque es el
          desempate: dos roles que permiten lo mismo se eligen por acá. */}
      <Panel
        title="Qué registros ve cada uno"
        subtitle={
          alcances.length === 0
            ? 'No hay diferencias de alcance entre los dos'
            : `${alcances.length} ${alcances.length === 1 ? 'modelo donde ven distinto' : 'modelos donde ven distinto'}`
        }
        bodyClassName="p-0"
      >
        {alcances.length === 0 ? (
          <p className="p-4 text-sm text-fg-muted">
            Los dos roles arrastran exactamente las mismas reglas de fila. Lo que uno vea de más o de
            menos va a salir de los permisos de arriba, no del filtro de registros.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[36rem] text-left">
              <caption className="sr-only">
                Diferencias de alcance de registros entre {a.name} y {b.name}
              </caption>
              <thead>
                <tr className="border-b border-line bg-app-subtle text-2xs uppercase tracking-[0.08em] text-fg-faint">
                  <th scope="col" className="px-4 py-2 font-semibold">
                    Qué
                  </th>
                  <th scope="col" className="px-4 py-2 font-semibold">
                    {a.name} ve
                  </th>
                  <th scope="col" className="px-4 py-2 font-semibold">
                    {b.name} ve
                  </th>
                </tr>
              </thead>
              <tbody>
                {alcances.map((fila) => (
                  <tr key={fila.model} className="border-b border-line/60 align-top last:border-b-0">
                    <th scope="row" className="px-4 py-2 font-normal">
                      <span className="block text-sm">{fila.label}</span>
                      <Codigo>{fila.model}</Codigo>
                    </th>
                    <td className="px-4 py-2">
                      <CeldaDeAlcance reglas={fila.soloA} abre={fila.abreA} />
                    </td>
                    <td className="px-4 py-2">
                      <CeldaDeAlcance reglas={fila.soloB} abre={fila.abreB} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="border-t border-line px-4 py-2.5 text-xs text-fg-muted">
              Sólo se listan las reglas que uno tiene y el otro no. Las que comparten —el aislamiento por
              organización, típicamente— no entran acá porque no ayudan a elegir. La lista completa de
              cada rol está en su ficha.
            </p>
          </div>
        )}
      </Panel>

      {iguales.length > 0 && (
        <Panel
          title="En qué son iguales"
          subtitle={`${iguales.length} ${iguales.length === 1 ? 'cosa' : 'cosas'} que ambos permiten igual`}
          actions={
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setVerComunes((valor) => !valor);
              }}
              aria-expanded={verComunes}
            >
              {verComunes ? 'Ocultar' : 'Ver detalle'}
            </Button>
          }
        >
          {verComunes ? (
            <ul>
              {iguales.map((fila) => (
                <li
                  key={fila.model}
                  className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 border-b border-line/60 py-2 last:border-b-0"
                >
                  <span className="text-sm">{fila.label}</span>
                  <Chips verbos={fila.comunes} />
                </li>
              ))}
            </ul>
          ) : (
            <>
              <Etiqueta as="p">Resumen</Etiqueta>
              <p className="text-sm text-fg-muted">
                Coinciden en {iguales.length} {iguales.length === 1 ? 'permiso' : 'permisos'}. La decisión
                entre los dos está en la tabla de arriba.
              </p>
            </>
          )}
        </Panel>
      )}
    </div>
  );
}
